/**
 * Hair Quiz Results - Display filtered and sorted product recommendations
 * @description Fetches products by handles from querystring and renders them in categories
 */

(function () {
  'use strict';

  /**
   * Decode HTML entities in a string (e.g., &#39; -> ')
   * @param {string} text - Text with potential HTML entities
   * @returns {string} Decoded text
   */
  function decodeHtmlEntities(text) {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  /**
   * Get category translation from Liquid-provided translations
   * @param {string} categoryKey - Category key (e.g., 'shampoo')
   * @returns {string} Translated category name
   */
  function getCategoryTranslation(categoryKey) {
    if (window.hairQuizCategoriesTranslations && window.hairQuizCategoriesTranslations[categoryKey]) {
      return decodeHtmlEntities(window.hairQuizCategoriesTranslations[categoryKey]);
    }
    return categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
  }

  /**
   * Get NEED tag translation from Liquid-provided translations
   * @param {string} needTag - NEED tag (e.g., 'NEED-shine')
   * @returns {string} Translated need phrase
   */
  function getNeedTranslation(needTag) {
    if (window.hairQuizNeedsTranslations && window.hairQuizNeedsTranslations[needTag]) {
      return decodeHtmlEntities(window.hairQuizNeedsTranslations[needTag]);
    }
    return needTag.replace('NEED-', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Update dynamic phrase based on NEED tags from querystring
   * @param {Array<string>} tags - Quiz tags from URL
   */
  function updateDynamicPhrase(tags) {
    const needPhraseEl = document.getElementById('quiz-need-phrase');
    if (!needPhraseEl) return;

    const needTag = tags.find(tag => tag.startsWith('NEED-'));
    if (!needTag) return;

    const phrase = getNeedTranslation(needTag);
    needPhraseEl.textContent = phrase;
  }

  /**
   * Build category mapping based on number of products
   * Uses shared utility from hairQuizUtils, with fallback for safety
   * @param {number} productCount - Number of products
   * @returns {Array<number>} Array mapping product index to category index
   */
  function buildCategoryMapping(productCount) {
    if (window.hairQuizUtils?.buildCategoryMapping) {
      return window.hairQuizUtils.buildCategoryMapping(productCount);
    }
    // Fallback if utils not loaded
    if (productCount <= 5) {
      return [0, 1, 2, 4, 5]; // Missing Primer (index 3)
    }
    return [0, 1, 2, 3, 4, 5]; // All 6 categories
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Generate responsive image srcset
   * @param {string} imgSrc - Base image URL
   * @returns {string} Srcset string
   */
  function buildSrcset(imgSrc) {
    if (!imgSrc) return '';
    const baseUrl = imgSrc.split('?')[0];
    return [
      `${baseUrl}?width=5000 5000w`,
      `${baseUrl}?width=4000 4000w`,
      `${baseUrl}?width=3000 3000w`,
      `${baseUrl}?width=2000 2000w`,
      `${baseUrl}?width=1600 1600w`,
      `${baseUrl}?width=1200 1200w`,
      `${baseUrl}?width=800 800w`,
      `${baseUrl}?width=600 600w`,
      `${baseUrl}?width=400 400w`
    ].join(', ');
  }

  /**
   * Extract clean description from product (remove split content)
   * @param {string} description - Product description HTML
   * @returns {string} Clean text description
   */
  function getCleanDescription(description) {
    if (!description) return '';

    let cleanDesc = description;
    if (description.includes('<!-- split -->')) {
      cleanDesc = description.split('<!-- split -->')[0];
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanDesc;
    return (tempDiv.textContent || tempDiv.innerText || '').trim();
  }

  /**
   * Render wishlist buttons (logged in vs not logged in)
   * Checks customer wishlist state to set correct initial visibility
   * @param {boolean} isLoggedIn - Customer logged in status
   * @param {string} handle - Product handle
   * @param {string} sku - Variant SKU
   * @returns {string} Wishlist buttons HTML
   */
  function renderWishlistButtons(isLoggedIn, handle, sku, productName) {
    const safeHandle = escapeHtml(handle);
    const safeSku = escapeHtml(sku);

    const addToWishlistText = window.hairQuizTranslations?.addToWishlist?.replace('{{ product_name }}', productName) || 'Add to wishlist';
    const removeFromWishlistText = window.hairQuizTranslations?.removeFromWishlist?.replace('{{ product_name }}', productName) || 'Remove from wishlist';

    if (isLoggedIn) {
      // Check if product is already in wishlist
      const wishlistHandles = window.hairQuizCustomerWishlist || [];
      const isInWishlist = wishlistHandles.includes(handle);

      // Set display states based on wishlist status
      const addButtonStyle = isInWishlist ? 'style="display: none;"' : '';
      const removeButtonStyle = isInWishlist ? '' : 'style="display: none;"';

      return `
        <button
          ${addButtonStyle}
          class="wishlist-add-icon btn-to-wishlist wishlist-listing"
          aria-label="${addToWishlistText}"
          data-sku="${safeSku}"
          type="button"
          data-tag="${safeHandle}"
          onclick="if(typeof dataLayer !== 'undefined' && typeof GTM !== 'undefined') { dataLayer.push({ 'event': 'GAevent', 'ss_event_id': GTM.getGtmRandomString(), 'eventID': '27', 'eventCategory': 'product', 'eventAction': 'add_to_wishlist', 'sku': '${safeSku}', 'eventLabel': 'hair_quiz' }); } if(typeof middleware !== 'undefined' && middleware.wishlist) { middleware.wishlist.add('${safeHandle}', '${safeSku}', event); }"
        >
          <svg width="20" height="17" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38.05 39.26">
            <path fill="#333" d="m23.37,34.18c.8-1.21,1.72-2.33,2.75-3.36.76-.79,1.5-1.64,2.19-2.53.56-.73,1.11-1.48,1.67-2.25l.41-.56c.33-.45.65-.89.98-1.33.97-1.27,2.05-2.49,3.2-3.61l.26-.28c2.63-3.31,3.73-7.61,3-11.79h0c-.19-1.19-.52-2.33-.97-3.39C35.76,2.25,33.09.27,30.05.02c-4.02-.2-7.88,1.43-10.56,4.45l-.32.36-.27-.4c-1.2-1.78-3.02-3.13-5.12-3.79-.85-.21-1.79-.32-2.84-.34-.16,0-.33-.01-.49-.01C6.26.28,2.41,2.79.77,6.65c-2.16,5.31.65,10.78,3.39,14.43,1.14,1.54,2.38,3.05,3.47,4.39,1.98,2.32,3.81,4.78,5.45,7.31l.14.22c1.05,2.19,2.65,4.01,4.66,5.3l.1.1c.3.43.77.73,1.29.82.64.13,1.16-.07,1.58-.5l.14-.18c.52-.78.99-1.62,1.37-2.47.29-.58.64-1.29,1.01-1.89Zm-3.15.86c-.17.37-.36.74-.56,1.1l-.2.35-.34-.22c-1.63-1.06-2.94-2.57-3.79-4.35l-.25-.42c-1.71-2.63-3.61-5.19-5.65-7.58-1.09-1.34-2.29-2.8-3.37-4.27-2.37-3.16-4.83-7.83-3.1-12.1,1.33-3.12,4.49-5.09,7.94-4.89h0c.75,0,1.53.09,2.22.25,1.92.61,3.5,2,4.36,3.82l1.46,2.41,1.61-2.21c2.17-3.02,5.64-4.70,9.34-4.55,2.12.18,3.97,1.58,4.75,3.58.39.91.67,1.88.83,2.89.61,3.5-.29,7.09-2.47,9.87l-.12.13c-1.23,1.19-2.38,2.49-3.42,3.86-.32.42-.62.85-.93,1.27l-.45.61c-.55.75-1.1,1.5-1.66,2.23-.64.83-1.32,1.61-2.01,2.32-1.13,1.13-2.16,2.39-3.05,3.74-.43.69-.78,1.38-1.15,2.15Z"/>
          </svg>
        </button>
        <button
          ${removeButtonStyle}
          class="wishlist-remove-icon btn-to-wishlist wishlist-listing"
          aria-label="${removeFromWishlistText}"
          data-sku="${safeSku}"
          type="button"
          data-tag="${safeHandle}"
          onclick="if(typeof middleware !== 'undefined' && middleware.wishlist) { middleware.wishlist.remove('${safeHandle}', '${safeSku}', event); }"
        >
          <svg width="20" height="17" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38.79 40">
            <path fill="#333" d="m38.57,8.78c-.19-1.21-.53-2.39-1-3.48C36.43,2.36,33.64.28,30.46.02c-4.19-.2-8.11,1.47-10.87,4.58-1.25-1.86-3.12-3.24-5.32-3.94-.89-.22-1.88-.34-2.94-.36C6.79.08,2.58,2.7.8,6.87c-2.23,5.48.64,11.08,3.43,14.8,1.14,1.54,2.38,3.06,3.48,4.41,1.96,2.3,3.79,4.75,5.42,7.26l.13.21c1.06,2.22,2.71,4.09,4.78,5.42.36.52.92.87,1.54.97.13.03.27.04.4.04.56,0,1.09-.22,1.51-.65l.17-.21c.54-.82,1.03-1.68,1.42-2.54.34-.7.66-1.32.99-1.85.78-1.18,1.69-2.28,2.7-3.3.78-.81,1.53-1.67,2.22-2.56.58-.75,1.14-1.52,1.7-2.29l.33-.45c.34-.47.69-.94,1.03-1.4.95-1.25,2.01-2.44,3.16-3.57l.27-.29c2.71-3.41,3.84-7.83,3.09-12.11Z"/>
          </svg>
        </button>
      `;
    }

    return `
      <button
        class="wishlist-add-icon btn-to-wishlist wishlist-listing"
        aria-label="${addToWishlistText}"
        type="button"
        data-sku="${safeSku}"
        onclick="if(typeof middleware !== 'undefined' && middleware.wishlist) { middleware.wishlist.message(event); }"
      >
        <svg width="20" height="17" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38.05 39.26">
          <path fill="#333" d="m23.37,34.18c.8-1.21,1.72-2.33,2.75-3.36.76-.79,1.5-1.64,2.19-2.53.56-.73,1.11-1.48,1.67-2.25l.41-.56c.33-.45.65-.89.98-1.33.97-1.27,2.05-2.49,3.2-3.61l.26-.28c2.63-3.31,3.73-7.61,3-11.79h0c-.19-1.19-.52-2.33-.97-3.39C35.76,2.25,33.09.27,30.05.02c-4.02-.2-7.88,1.43-10.56,4.45l-.32.36-.27-.4c-1.2-1.78-3.02-3.13-5.12-3.79-.85-.21-1.79-.32-2.84-.34-.16,0-.33-.01-.49-.01C6.26.28,2.41,2.79.77,6.65c-2.16,5.31.65,10.78,3.39,14.43,1.14,1.54,2.38,3.05,3.47,4.39,1.98,2.32,3.81,4.78,5.45,7.31l.14.22c1.05,2.19,2.65,4.01,4.66,5.3l.1.1c.3.43.77.73,1.29.82.64.13,1.16-.07,1.58-.5l.14-.18c.52-.78.99-1.62,1.37-2.47.29-.58.64-1.29,1.01-1.89Zm-3.15.86c-.17.37-.36.74-.56,1.1l-.2.35-.34-.22c-1.63-1.06-2.94-2.57-3.79-4.35l-.25-.42c-1.71-2.63-3.61-5.19-5.65-7.58-1.09-1.34-2.29-2.8-3.37-4.27-2.37-3.16-4.83-7.83-3.1-12.1,1.33-3.12,4.49-5.09,7.94-4.89h0c.75,0,1.53.09,2.22.25,1.92.61,3.5,2,4.36,3.82l1.46,2.41,1.61-2.21c2.17-3.02,5.64-4.7,9.34-4.55,2.12.18,3.97,1.58,4.75,3.58.39.91.67,1.88.83,2.89.61,3.5-.29,7.09-2.47,9.87l-.12.13c-1.23,1.19-2.38,2.49-3.42,3.86-.32.42-.62.85-.93,1.27l-.45.61c-.55.75-1.1,1.5-1.66,2.23-.64.83-1.32,1.61-2.01,2.32-1.13,1.13-2.16,2.39-3.05,3.74-.43.69-.78,1.38-1.15,2.15Z"/>
        </svg>
      </button>
    `;
  }
  /**
   * Render single product slide HTML (simplified, reuses existing patterns)
   * @param {Object} product - Product object from Shopify API
   * @param {number} productIndex - Index in the product array
   * @param {number} categoryIndex - Category index for the title
   * @returns {string} Product HTML for swiper slide
   */
  function renderProduct(product, productIndex, categoryIndex) {
    try {
      // Extract product data
      const variant = product.variants?.[0];
      if (!variant) return '';

      const price = variant.price;
      const compareAtPrice = variant.compare_at_price;
      const available = variant.available;
      const image = product.featured_image || product.images?.[0] || '';
      const variantId = variant.id || '';
      const variantSku = variant.sku || '';

      // Format prices with dynamic currency symbol from Shopify
      const currencySymbol = window.shopCurrency?.symbol || '€';
      let formattedPrice;
      if (window.shopCountry === 'FR' || window.shopCountry === 'IT') {
        formattedPrice = `${(price / 100).toFixed(2).replace('.', ',')}${currencySymbol}`;
      } else {
        formattedPrice = `${currencySymbol}${(price / 100).toFixed(2)}`;
      }

      let formattedCompareAtPrice;
      if (compareAtPrice) {
        if (window.shopCountry === 'FR' || window.shopCountry === 'IT') {
          formattedCompareAtPrice = `${(compareAtPrice / 100).toFixed(2).replace('.', ',')}${currencySymbol}`;
        } else {
          formattedCompareAtPrice = `${currencySymbol}${(compareAtPrice / 100).toFixed(2)}`;
        }
      } else {
        formattedCompareAtPrice = null;
      }

      // Format unit price
      let unitPriceHtml = '';
      if (available) {
        if (variant.unit_price_measurement) {
          const unitPrice = variant.unit_price || 0;
          let formattedUnitPrice;
          if (window.shopCountry === 'FR' || window.shopCountry === 'IT') {
            formattedUnitPrice = `${(unitPrice / 100).toFixed(2).replace('.', ',')}${currencySymbol}`;
          } else {
            formattedUnitPrice = `${currencySymbol}${(unitPrice / 100).toFixed(2)}`;
          }
          const refValue = variant.unit_price_measurement.reference_value;
          const refUnit = variant.unit_price_measurement.reference_unit;
          const measurement = (refValue !== 1 ? refValue : '') + refUnit;
          unitPriceHtml = `<div class="product-price-unit product-price-unit--available" data-unit-price-container="">
            <span>(${formattedUnitPrice}/${escapeHtml(measurement)})</span>
          </div>`;
        } else {
          const unitLabel = window.hairQuizTranslations?.unit || 'Unità';
          unitPriceHtml = `<div class="product-price-unit product-price-unit--available" data-unit-price-container="">
            <span>(${formattedPrice}/${escapeHtml(unitLabel)})</span>
          </div>`;
        }
      }

      // Escape data for safe HTML insertion
      const safeTitle = escapeHtml(product.title);
      const safeUrl = escapeHtml(product.url);
      const safeImage = escapeHtml(image);
      const safeVendor = escapeHtml(product.vendor || '');
      const safeSku = escapeHtml(variantSku) || '';

      // Clean description
      const cleanDescription = getCleanDescription(product.description);
      const safeDescription = escapeHtml(cleanDescription);

      // GTM tracking attributes
      const productName = product.title?.replace(/'/g, '').replace(/ /g, '_').toLowerCase() || '';
      const productCategory = product.product_type?.replace(/'/g, '').replace(/ /g, '_').toLowerCase() || '';
      const productVariant = variant.title?.replace(/'/g, '').replace(/ /g, '_').toLowerCase() || '';

      // Category title
      const categoryKey = window.hairQuizUtils?.PRODUCT_CATEGORY_KEYS[categoryIndex] || '';
      const categoryTitle = categoryKey ? getCategoryTranslation(categoryKey) : '';
      const safeCategoryTitle = escapeHtml(categoryTitle);

      // Image handling
      const srcset = buildSrcset(image);
      const lowQualityImage = image ? `${image.split('?')[0]}?width=50` : '';

      // Translations
      const addToCartText = window.hairQuizTranslations?.addToCart || 'Add to cart';

      // Render HTML
      return `
        <div class="swiper-slide list-swiper">
          <article class="product-wrap">
            <div class="product-wrap-container">
              ${safeCategoryTitle ? `<h3 class="quiz-product-category-title">${safeCategoryTitle}</h3>` : ''}
              <div
                class=""
                product-name="${productName}"
                product-id="${variantId}"
                product-sku="${safeSku}"
                product-price="${(price / 100).toFixed(2)}"
                product-vendor="${safeVendor}"
                product-category="${productCategory}"
                product-variant="${productVariant}"
                position-number="${productIndex + 1}"
                data-available="${available && price > 0}"
              >
                <div class="wishlist-wrap">
                  ${renderWishlistButtons(window.hairQuizCustomerLogged, product.handle, variantSku, product.title)}
                </div>
                <div class="relative product_image">
                  <a href="${safeUrl}" tabindex="-1" aria-hidden="true" data-no-instant>
                    <div class="image__container">
                      ${image ?
          `<div class="image-element__wrap" style="max-width: 800px;">
                          <img
                            alt="${safeTitle}"
                            src="${lowQualityImage}"
                            data-src="${safeImage}?width=1600"
                            data-sizes="auto"
                            data-srcset="${srcset}"
                            class="lazyload transition--blur-up"
                            loading="lazy"
                          />
                        </div>` :
          '<div style="padding: 60px 20px; text-align: center; color: #ccc;">No image</div>'}
                    </div>
                  </a>
                </div>
              </div>

              <div class="product-info product-details-info">
                <a class="product-info__caption" href="${safeUrl}" data-no-instant>
                  <p class="product_name">${safeTitle}</p>
                </a>
                ${safeDescription ? `<span class="description">${safeDescription}</span>` : ''}
                <div class="product-details-info__bottom-row">
                  <span class="price ${compareAtPrice ? 'sale' : ''}">
                    ${compareAtPrice ? `<s class="was_price">${formattedCompareAtPrice}</s>` : ''}
                    <span class="money">${formattedPrice}</span>
                  </span>
                  ${unitPriceHtml}
                </div>
              </div>

              ${available && price > 0 ?
          `<div class="quiz-product-add-to-cart">
                  <button
                    type="button"
                    class="btn-primary quiz-add-single-btn"
                    data-variant-id="${variantId}"
                    onclick="window.hairQuizResults?.addSingleToCart(${variantId}, this)"
                  >
                    <span class="btn-primary__text anim">${addToCartText}</span>
                  </button>
                </div>` : ''}
            </div>
          </article>
        </div>
      `;
    } catch (error) {
      console.error('Error rendering product:', error);
      return '';
    }
  }

  /**
   * Render all products to the swiper carousel
   * @param {Array<Object>} products - Products fetched from querystring handles
   */
  function renderProducts(products) {
    const containerEl = document.getElementById('quiz-results-products');
    if (!containerEl) return;

    containerEl.innerHTML = '';

    if (products.length === 0) {
      containerEl.innerHTML = '<div class="swiper-slide" style="padding: 40px; text-align: center; color: #999;">No products found matching your quiz results.</div>';
      return;
    }

    const categoryMapping = buildCategoryMapping(products.length);
    const productsHTML = products
      .map((product, productIndex) => {
        const categoryIndex = categoryMapping[productIndex];
        return renderProduct(product, productIndex, categoryIndex);
      })
      .join('');

    containerEl.innerHTML = productsHTML;
    initializeSwiper();
  }

  /**
   * Initialize Swiper carousel using productSliderHelper
   * Triggers setWrapperHeight event for external height recalculation
   */
  function initializeSwiper() {
    const sliderWrapper = document.querySelector('.quiz-results-products-slider');
    if (!sliderWrapper) return;

    const swiperContainer = sliderWrapper.querySelector('.quiz-results-swiper');
    if (!swiperContainer) return;

    // Use productSliderHelper to initialize with all features (opacity, height management, etc.)
    if (typeof productSliderHelper !== 'undefined' && productSliderHelper.initSlider) {
      productSliderHelper.initSlider(sliderWrapper, '.quiz-results-swiper');
    }

    // Trigger initial height calculation
    if (typeof $ !== 'undefined') {
      $(window).trigger('setWrapperHeight');

      // Recalculate after images load (lazy loading)
      setTimeout(() => {
        $(window).trigger('setWrapperHeight');
      }, 300);

      setTimeout(() => {
        $(window).trigger('setWrapperHeight');
      }, 800);
    }
  }

  /**
   * Add a single product to cart via Shopify Cart API
   * @param {number} variantId - Variant ID to add
   * @param {number} quantity - Quantity to add (default: 1)
   * @returns {Promise<Object>} Cart response from Shopify
   */
  async function addToCart(variantId, quantity = 1) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to add to cart: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Handle post-add-to-cart actions: show toast, refresh cart, open minicart
   * @param {string} toastMessage - Message to show in toast notification
   */
  async function handleCartUpdate(toastMessage) {
    // Show success toast message
    if (typeof middleware !== 'undefined' && middleware.general && middleware.general.success_Toast) {
      middleware.general.success_Toast(toastMessage);
    }

    // Refresh cart and open minicart
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    // Refresh cart content
    if (typeof refreshCart === 'function') {
      refreshCart(cart);
    }

    // Open minicart on desktop (only above 1024px)
    if (window.innerWidth > 1024) {
      let $cartContainer;

      if ($('#header').is(':visible')) {
        $cartContainer = $('#header .cart_container');
      } else if ($('.sticky_nav--stick').length) {
        $cartContainer = $('.sticky_nav .cart_container');
      } else {
        $cartContainer = $('.header .cart_container');
      }

      if (typeof window.openMiniCart === 'function' && $cartContainer.length) {
        window.openMiniCart($cartContainer);
      }
    }
  }

  /**
   * Add a single product to cart (called from individual Add to Cart buttons)
   * @param {number} variantId - Variant ID to add
   * @param {HTMLElement} button - Button element clicked
   */
  async function addSingleToCart(variantId, button) {
    if (!variantId || !button) return;

    const originalHTML = button.innerHTML;
    const addingText = window.hairQuizTranslations?.adding || 'Adding...';
    const addedText = window.hairQuizTranslations?.addedToCart || 'Added to cart';

    try {
      button.disabled = true;
      button.innerHTML = `<span class="btn-primary__text anim">${addingText}</span>`;

      await addToCart(variantId, 1);

      // DataLayer push for addToCart event
      const dataEl = button.closest('.product-wrap')?.querySelector('[product-id]');
      if (dataEl && typeof dataLayer !== 'undefined' && typeof GTM !== 'undefined') {
        dataLayer.push({
          'event': 'addToCart',
          'ss_event_id': GTM.getGtmRandomString(),
          'addType': 'product',
          'ecommerce': {
            'currencyCode': GTM.currency_code,
            'add': {
              'actionField': {
                'list': 'hair_quiz'
              },
              'products': [{
                'name': dataEl.getAttribute('product-name'),
                'id': dataEl.getAttribute('product-sku'),
                'price': parseFloat(dataEl.getAttribute('product-price')),
                'brand': dataEl.getAttribute('product-vendor'),
                'category': dataEl.getAttribute('product-category'),
                'variant': dataEl.getAttribute('product-variant'),
                'quantity': 1
              }]
            }
          }
        });
      }

      // Update button to show success state
      button.innerHTML = `<span class="btn-primary__text anim">${addedText}</span>`;

      // Handle cart update (toast, refresh, open minicart)
      const toastMessage = window.hairQuizTranslations?.addedToCart || 'Added to cart';
      await handleCartUpdate(toastMessage);

      // Reset button after 2 seconds
      setTimeout(() => {
        button.disabled = false;
        button.innerHTML = originalHTML;
      }, 2000);

    } catch (error) {
      console.error('Error adding product to cart:', error);
      button.disabled = false;
      button.innerHTML = originalHTML;

      // Show error message like PDP (not alert)
      const $buttonWrap = $(button).closest('.quiz-product-add-to-cart');
      $('.warning', $buttonWrap).remove();

      const errorMessage = window.hairQuizTranslations?.addToCartError || 'Product not available';
      const warning = '<p class="warning animated bounceIn" style="color: #d54d4d; margin-top: 10px; text-align: center;">' + errorMessage + '</p>';
      $buttonWrap.append(warning);

      // Remove warning after 5 seconds
      setTimeout(() => {
        $('.warning', $buttonWrap).fadeOut(300, function () {
          $(this).remove();
        });
      }, 5000);
    }
  }

  /**
   * Add all displayed products to cart (batch add)
   * Uses a single API call to add all products at once
   */
  async function addAllToCart() {
    const productWraps = document.querySelectorAll('#quiz-results-products .product-wrap');
    if (productWraps.length === 0) return;

    const addButton = document.getElementById('quiz-add-all-button');
    const originalHTML = addButton?.innerHTML || '';
    const addingText = window.hairQuizTranslations?.adding || 'Adding...';
    const addedText = window.hairQuizTranslations?.addedToCart || 'Added to cart';

    if (addButton) {
      addButton.disabled = true;
      addButton.innerHTML = `<span class="btn-primary__text anim">${addingText}</span>`;
    }

    // Extract data elements (inner divs with GTM attributes)
    const dataEls = Array.from(productWraps).map(wrap => wrap.querySelector('[product-id]')).filter(Boolean);

    // Extract all valid variant IDs from AVAILABLE products only
    const variantIds = dataEls
      .filter(el => {
        const isAvailable = el.getAttribute('data-available') === 'true';
        const variantId = el.getAttribute('product-id');
        return isAvailable && variantId && variantId !== 'null' && variantId !== '';
      })
      .map(el => el.getAttribute('product-id'));

    if (variantIds.length === 0) {
      if (addButton) {
        addButton.disabled = false;
        addButton.innerHTML = originalHTML;
      }

      // Show message that no products are available
      const $buttonWrap = $(addButton).closest('.quiz-add-all-wrap');
      $('.warning', $buttonWrap).remove();
      const errorMessage = window.hairQuizTranslations?.noAvailableProducts || 'No products available to add';
      const warning = '<p class="warning animated bounceIn" style="color: #d54d4d; margin-top: 10px; text-align: center;">' + errorMessage + '</p>';
      $buttonWrap.append(warning);

      setTimeout(() => {
        $('.warning', $buttonWrap).fadeOut(300, function () {
          $(this).remove();
        });
      }, 5000);

      return;
    }

    // Build items array for batch add
    const items = variantIds.map(variantId => ({
      id: parseInt(variantId),
      quantity: 1
    }));

    try {
      // Single API call to add all products
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        throw new Error(`Failed to add products to cart: ${response.status}`);
      }

      const result = await response.json();

      // DataLayer push for addToCart event with all products
      if (typeof dataLayer !== 'undefined' && typeof GTM !== 'undefined') {
        const productsData = Array.from(productWraps).map(wrap => ({
          'name': wrap.getAttribute('product-name'),
          'id': wrap.getAttribute('product-sku'),
          'price': parseFloat(wrap.getAttribute('product-price')),
          'brand': wrap.getAttribute('product-vendor'),
          'category': wrap.getAttribute('product-category'),
          'variant': (wrap.getAttribute('product-variant')),
          'quantity': 1
        }));

        dataLayer.push({
          'event': 'addToCart',
          'ss_event_id': GTM.getGtmRandomString(),
          'addType': 'product',
          'ecommerce': {
            'currencyCode': GTM.currency_code,
            'add': {
              'actionField': {
                'list': 'hair_quiz'
              },
              'products': productsData
            }
          }
        });
      }

      // Update button to show success state
      if (addButton) {
        addButton.innerHTML = `<span class="btn-primary__text anim">${addedText}</span>`;
      }

      // Handle cart update (toast, refresh, open minicart)
      // Check if some products were OOS (not added)
      const totalProducts = productWraps.length;
      const addedProducts = variantIds.length;
      let toastMessage;

      if (addedProducts < totalProducts) {
        toastMessage = window.hairQuizTranslations?.someAddedToCart || `${addedProducts} products added to cart`;
      } else {
        toastMessage = window.hairQuizTranslations?.allAddedToCart || 'All products added to cart';
      }

      await handleCartUpdate(toastMessage);

      // Reset button after 2 seconds
      setTimeout(() => {
        if (addButton) {
          addButton.disabled = false;
          addButton.innerHTML = originalHTML;
        }
      }, 2000);


    } catch (error) {
      if (addButton) {
        addButton.disabled = false;
        addButton.innerHTML = originalHTML;
      }

      // Show error message
      const $buttonWrap = $(addButton).closest('.quiz-add-all-wrap');
      $('.warning', $buttonWrap).remove();

      const errorMessage = window.hairQuizTranslations?.noProductsAdded || 'Could not add products to cart';
      const warning = '<p class="warning animated bounceIn" style="color: #d54d4d; margin-top: 10px; text-align: center;">' + errorMessage + '</p>';
      $buttonWrap.append(warning);

      // Remove warning after 5 seconds
      setTimeout(() => {
        $('.warning', $buttonWrap).fadeOut(300, function () {
          $(this).remove();
        });
      }, 5000);
    }
  }

  /**
   * Setup event listeners for action buttons
   */
  function setupButtonHandlers() {
    const addAllButton = document.getElementById('quiz-add-all-button');
    if (addAllButton) {
      addAllButton.addEventListener('click', addAllToCart);
    }
  }

  /**
   * Main initialization - Fetches products from querystring and renders them
   */
  async function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Get handles and tags from querystring
    const { handles, tags } = window.hairQuizUtils.getUrlParams();
    if (handles.length === 0) return;

    try {
      // Fetch products by handles
      const products = await window.hairQuizUtils.fetchProducts(handles);
      if (products.length === 0) return;

      // Save SKU-category mapping for newsletter form
      window.hairQuizUtils.saveSkuToCategoryMap(products);
      window.hairQuizProducts = products;

      // Render products in swiper carousel
      renderProducts(products);

      // Update dynamic phrase with NEED translation
      updateDynamicPhrase(tags);

      // Setup button handlers
      setupButtonHandlers();

    } catch (error) {
      console.error('Error initializing quiz results:', error);
    }
  }

  // Expose public API for inline onclick handlers
  window.hairQuizResults = {
    addSingleToCart,
    addAllToCart,
  };

  // Initialize when DOM is ready
  init();
})();
